import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Input, Select, TextArea, Typography } from '@/components';
import { BESSProjectTechnology } from '@/features/projects/types';
import { nullableStringSetter, getDateFromQuarterAndYear, getQuarterFromDate, getYearFromDate } from '@/helpers';
import { useDevelopmentStatus } from '@/hooks';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type BessFormFields = {
  development_status: string | null;
  installed_capacity: number | null;
  injection_capacity: number | null;
  consumption_capacity: number | null;
  ready_to_build: string | null;
  ready_to_build_quarter: string | null;
  ready_to_build_year: number | null;
  commercial_operation_date: string | null;
  commercial_operation_date_quarter: string | null;
  commercial_operation_date_year: number | null;
  comments: string | null;
  storage_technology: string | null;
  storage_capacity: number | null;
  hours_cycles: number | null;
};

export type BessSubmitFields = {
  development_status: string | null;
  installed_capacity: number | null;
  injection_capacity: number | null;
  consumption_capacity: number | null;
  ready_to_build: string | null;
  commercial_operation_date: string | null;
  storage_technology: string | null;
  storage_capacity: number | null;
  hours_cycles: number | null;
  comments: string | null;
  item?: any
};

export type BessFormProps = {
  readonly id: string;
  readonly item: BESSProjectTechnology;
  readonly onSubmit: (data: BessSubmitFields) => void;
};

export function BessFormNew({ id, item, onSubmit }: BessFormProps) {
  const { t } = useTranslation(['projects']);
  const { options: developmentStatusOptions } = useDevelopmentStatus();

  const quarterOptions = [
    { label: 'Q1', value: '1' },
    { label: 'Q2', value: '2' },
    { label: 'Q3', value: '3' },
    { label: 'Q4', value: '4' },
  ];

  const options = useMemo(
    () => ({
      defaultValues: {
        development_status: item.development_status ?? null,
        installed_capacity: item.installed_capacity ?? null,
        injection_capacity: item.injection_capacity ?? null,
        consumption_capacity: item.consumption_capacity ?? null,
        ready_to_build: item.ready_to_build ?? null,
        ready_to_build_quarter: getQuarterFromDate(item.ready_to_build),
        ready_to_build_year: getYearFromDate(item.ready_to_build),
        commercial_operation_date_quarter: getQuarterFromDate(item.commercial_operation_date),
        commercial_operation_date_year: getYearFromDate(item.commercial_operation_date),
        commercial_operation_date: item.commercial_operation_date ?? null,
        comments: item.comments ?? null,
        storage_technology: item?.storage_technology ?? null,
        storage_capacity: item?.storage_capacity ?? null,
        hours_cycles: item?.hours_cycles ?? null,
      },
    }),
    [item]
  );

  const onSubmitBessForm = useCallback(
    (data: BessFormFields) => {
      data.ready_to_build = getDateFromQuarterAndYear(data.ready_to_build_quarter, data.ready_to_build_year);
      data.commercial_operation_date = getDateFromQuarterAndYear(
        data.commercial_operation_date_quarter,
        data.commercial_operation_date_year
      );

      const payload = {
        development_status: data.development_status,
        installed_capacity: data.installed_capacity,
        injection_capacity: data.injection_capacity,
        consumption_capacity: data.consumption_capacity,
        ready_to_build: data.ready_to_build,
        commercial_operation_date: data.commercial_operation_date,
        storage_technology: data.storage_technology,
        storage_capacity: data.storage_capacity,
        hours_cycles: data.hours_cycles,
        comments: data.comments,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<BessFormFields> id={id} onSubmit={(data) => onSubmitBessForm(data)} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 italic">
            {t(
              'Please fill in this section with the main details about the project, including technology, development status, location, installed capacity, ready-to-build (RtB) and COD dates, etc.'
            )}
          </Typography>
          <Field
            id="development_status"
            label={t('Development status')}
            error={formState.errors['development_status']}
            delay={0}
          >
            <Select
              id="development_status"
              registration={register('development_status', { setValueAs: nullableStringSetter })}
              options={developmentStatusOptions}
              placeholder={t('Select a development status')}
              color={formState.errors['development_status'] ? 'error' : 'primary'}
            />
          </Field>
          <Field
            id="installed_capacity"
            label={t('Installed Capacity')}
            error={formState.errors['installed_capacity']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="installed_capacity"
              type="number"
              suffix="MW"
              placeholder={t('Example: 300')}
              color={formState.errors['installed_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('installed_capacity', {
                ...NUMERIC,
              })}
            />
          </Field>

          <Field
            id="injection_capacity"
            label={t('Injection Capacity')}
            error={formState.errors['injection_capacity']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="injection_capacity"
              type="number"
              suffix="MW"
              placeholder={t('Example: 300')}
              color={formState.errors['injection_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('injection_capacity', {
                ...NUMERIC,
              })}
            />
          </Field>

          <Field
            id="consumption_capacity"
            label={t('Consumption Capacity')}
            error={formState.errors['consumption_capacity']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="consumption_capacity"
              type="number"
              suffix="MW"
              placeholder={t('Example: 300')}
              color={formState.errors['consumption_capacity'] ? 'error' : 'primary'}
              autoComplete="off"
              registration={register('consumption_capacity', {
                ...NUMERIC,
              })}
            />
          </Field>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Field
              id="ready_to_build"
              label={t('Ready to Build Date')}
              error={formState.errors['ready_to_build']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="ready_to_build_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['ready_to_build_quarter'] ? 'error' : 'primary'}
                  registration={register('ready_to_build_quarter')}
                />
                <Input
                  id="ready_to_build_year"
                  type="number"
                  step="1"
                  color={formState.errors['ready_to_build_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('ready_to_build_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>

            <Field
              id="commercial_operation_date"
              label={t('Commercial Operation Date')}
              error={formState.errors['ready_to_build']}
              delay={0.25}
              classes={{ field: 'space-y-1' }}
            >
              <div className="flex gap-1">
                <Select
                  id="commercial_operation_date_quarter"
                  options={quarterOptions}
                  placeholder={t('Select a quarter')}
                  color={formState.errors['commercial_operation_date_quarter'] ? 'error' : 'primary'}
                  registration={register('commercial_operation_date_quarter')}
                />
                <Input
                  id="commercial_operation_date_year"
                  type="number"
                  step="1"
                  color={formState.errors['commercial_operation_date_year'] ? 'error' : 'primary'}
                  autoComplete="off"
                  registration={register('commercial_operation_date_year', { ...NUMERIC })}
                  placeholder={t('Year')}
                />
              </div>
            </Field>
          </div>
          <Field
            id="storage_technology"
            label={t('Storage technology')}
            error={formState.errors['storage_technology']}
            delay={0}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="storage_technology"
              type="text"
              color={formState.errors['storage_technology'] ? 'error' : 'primary'}
              registration={register('storage_technology')}
            />
          </Field>

          <Field
            id="storage_capacity"
            label={t('Storage capacity')}
            error={formState.errors['storage_capacity']}
            delay={0.15}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="storage_capacity"
              type="number"
              step="0.1"
              suffix="MWh"
              color={formState.errors['storage_capacity'] ? 'error' : 'primary'}
              registration={register('storage_capacity', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="hours_cycles"
            label={t('Hours cycles')}
            error={formState.errors['hours_cycles']}
            delay={0.3}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="hours_cycles"
              type="number"
              suffix="h"
              color={formState.errors['hours_cycles'] ? 'error' : 'primary'}
              registration={register('hours_cycles', { ...NUMERIC })}
            />
          </Field>

          <Field
            id="comments"
            label={t('Comments')}
            error={formState.errors['comments']}
            delay={0.45}
            classes={{ field: 'space-y-1' }}
          >
            <TextArea
              id="comments"
              color={formState.errors['comments'] ? 'error' : 'primary'}
              registration={register('comments', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
